import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/api';
import axios from 'axios';
import './AuditLogs.css';

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [tableFilter, setTableFilter] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    loadLogs();
  }, [offset, actionFilter, tableFilter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/audit-logs?limit=${limit}&offset=${offset}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (tableFilter) url += `&table=${tableFilter}`;

      const response = await axios.get(url);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Bangkok',
    });
  };

  const formatJSON = (jsonString) => {
    if (!jsonString) return null;
    try {
      const obj = JSON.parse(jsonString);
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  const parseData = (jsonString) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return null;
    }
  };

  const getReadableSummary = (log) => {
    const oldData = parseData(log.old_data);
    const newData = parseData(log.new_data);

    // Employee actions
    if (log.table_name === 'employees') {
      if (log.action === 'CREATE') {
        return `เพิ่มพนักงาน: ${newData?.employee_name || 'Unknown'} (ID: ${newData?.employee_id || log.record_id})`;
      } else if (log.action === 'UPDATE') {
        return `แก้ไขข้อมูลพนักงาน: ${oldData?.employee_name || newData?.employee_name || 'Unknown'}`;
      } else if (log.action === 'DELETE') {
        return `ลบพนักงาน: ${oldData?.employee_name || 'Unknown'} (ID: ${oldData?.employee_id || log.record_id})`;
      }
    }

    // Training record actions
    if (log.table_name === 'training_records') {
      const name = oldData?.employee_name || newData?.employee_name || oldData?.guest_name || newData?.guest_name || 'Unknown';
      const topic = oldData?.training_topic || newData?.training_topic || 'Unknown topic';

      if (log.action === 'CREATE' || log.action === 'INSERT') {
        return `บันทึกการอบรม: ${name} - ${topic}`;
      } else if (log.action === 'UPDATE') {
        return `แก้ไขการอบรม: ${name} - ${topic}`;
      } else if (log.action === 'DELETE') {
        return `ลบการอบรม: ${name} - ${topic}`;
      } else if (log.action === 'DELETE_SESSION') {
        const sessionData = oldData?.session_records;
        const count = sessionData?.length || 0;
        // Get training topic from first record
        const firstRecord = sessionData?.[0];
        const sessionTopic = firstRecord?.training_topic || 'Unknown topic';
        return `ลบ Training Session: ${sessionTopic} (${count} records)`;
      }
    }

    return `${log.action} on ${log.table_name}`;
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'CREATE':
      case 'INSERT':
        return 'badge-create';
      case 'UPDATE':
        return 'badge-update';
      case 'DELETE':
      case 'DELETE_SESSION':
        return 'badge-delete';
      default:
        return 'badge-default';
    }
  };

  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  if (loading && logs.length === 0) {
    return (
      <div className="audit-logs-container">
        <div className="loading-spinner">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div className="audit-logs-container">
      <div className="audit-logs-header">
        <div className="header-content">
          <h1 className="page-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            Audit Logs
          </h1>
          <p className="page-subtitle">System activity and change history / ประวัติการเปลี่ยนแปลงข้อมูล</p>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Action Type</label>
          <select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setOffset(0); }}>
            <option value="">All Actions</option>
            <option value="CREATE">CREATE</option>
            <option value="INSERT">INSERT</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
            <option value="DELETE_SESSION">DELETE SESSION</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Table</label>
          <select value={tableFilter} onChange={(e) => { setTableFilter(e.target.value); setOffset(0); }}>
            <option value="">All Tables</option>
            <option value="employees">Employees</option>
            <option value="training_records">Training Records</option>
          </select>
        </div>

        <button className="btn-clear-filters" onClick={() => { setActionFilter(''); setTableFilter(''); setOffset(0); }}>
          Clear Filters
        </button>
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-label">Total Logs:</span>
          <span className="stat-value">{total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Page:</span>
          <span className="stat-value">{currentPage} of {totalPages}</span>
        </div>
      </div>

      <div className="logs-list">
        {logs.length === 0 ? (
          <div className="empty-state">
            <p>No audit logs found</p>
          </div>
        ) : (
          logs.map((log) => {
            const oldData = parseData(log.old_data);
            const newData = parseData(log.new_data);

            return (
            <div key={log.id} className={`log-card ${expandedLog === log.id ? 'expanded' : ''}`}>
              <div className="log-header" onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                <div className="log-main-info">
                  <span className={`action-badge ${getActionBadgeClass(log.action)}`}>
                    {log.action}
                  </span>
                  <span className="log-summary">{getReadableSummary(log)}</span>
                </div>
                <div className="log-meta">
                  <span className="log-date">{formatDate(log.created_at)}</span>
                  <span className="log-ip">{log.ip_address}</span>
                  <svg
                    className={`expand-icon ${expandedLog === log.id ? 'rotated' : ''}`}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </div>

              {expandedLog === log.id && (
                <div className="log-details">
                  <div className="detail-summary">
                    <div className="summary-row">
                      <span className="label">Action:</span>
                      <span className="value">{log.action}</span>
                    </div>
                    <div className="summary-row">
                      <span className="label">Table:</span>
                      <span className="value">{log.table_name}</span>
                    </div>
                    <div className="summary-row">
                      <span className="label">Record ID:</span>
                      <span className="value">{log.record_id || 'N/A'}</span>
                    </div>
                    {log.user_name && (
                      <div className="summary-row highlight">
                        <span className="label">👤 Performed By:</span>
                        <span className="value">{log.user_name}</span>
                      </div>
                    )}
                    {log.user_email && (
                      <div className="summary-row highlight">
                        <span className="label">📧 Email:</span>
                        <span className="value">{log.user_email}</span>
                      </div>
                    )}
                    <div className="summary-row">
                      <span className="label">IP Address:</span>
                      <span className="value">{log.ip_address}</span>
                    </div>
                    <div className="summary-row">
                      <span className="label">Timestamp:</span>
                      <span className="value">{formatDate(log.created_at)}</span>
                    </div>
                    {oldData?.employee_name && (
                      <div className="summary-row">
                        <span className="label">Employee Name:</span>
                        <span className="value">{oldData.employee_name}</span>
                      </div>
                    )}
                    {newData?.employee_name && !oldData?.employee_name && (
                      <div className="summary-row">
                        <span className="label">Employee Name:</span>
                        <span className="value">{newData.employee_name}</span>
                      </div>
                    )}
                    {(oldData?.training_topic || newData?.training_topic) && (
                      <div className="summary-row">
                        <span className="label">Training Topic:</span>
                        <span className="value">{oldData?.training_topic || newData?.training_topic}</span>
                      </div>
                    )}
                    {(oldData?.training_date || newData?.training_date) && (
                      <div className="summary-row">
                        <span className="label">Training Date:</span>
                        <span className="value">{oldData?.training_date || newData?.training_date}</span>
                      </div>
                    )}
                  </div>

                  {log.old_data && (
                    <div className="data-section">
                      <h4>Old Data (ข้อมูลเดิม)</h4>
                      <pre className="json-data">{formatJSON(log.old_data)}</pre>
                    </div>
                  )}
                  {log.new_data && (
                    <div className="data-section">
                      <h4>New Data (ข้อมูลใหม่)</h4>
                      <pre className="json-data">{formatJSON(log.new_data)}</pre>
                    </div>
                  )}
                  {!log.old_data && !log.new_data && (
                    <div className="no-data">No additional data available</div>
                  )}
                </div>
              )}
            </div>
          );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn-page"
            onClick={handlePrevPage}
            disabled={offset === 0}
          >
            ← Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn-page"
            onClick={handleNextPage}
            disabled={offset + limit >= total}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default AuditLogs;
